import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { COMPANY } from "@/data/content";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const empty = { name: "", email: "", phone: "", subject: "", message: "" };

const Contact = () => {
  useDocumentTitle("Contact");
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within one business day.");
      setForm(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Talk to our engineering team."
        description="We're happy to discuss custom motors, controllers, embedded systems, AI, aerospace projects or training programs."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Contact" }]}
      />

      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
              Get in touch
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight leading-tight">
              We usually respond within one business day.
            </h2>

            <div className="mt-8 grid gap-4">
              {[
                { Icon: Mail, t: "Email", v: COMPANY.email },
                { Icon: Phone, t: "Phone", v: COMPANY.phone },
                { Icon: MapPin, t: "Address", v: COMPANY.address },
              ].map(({ Icon, t, v }) => (
                <div
                  key={t}
                  className="flex items-start gap-4 border border-slate-200 rounded-md p-4 bg-white"
                >
                  <div className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#0B1B3D]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                      {t}
                    </div>
                    <div className="text-sm font-medium text-[#0B1B3D]">{v}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="mt-8 rounded-xl overflow-hidden border border-slate-200">
              <iframe
                title="Akyvia Office"
                data-testid="contact-map"
                src={COMPANY.mapEmbed}
                className="w-full h-64"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 grid gap-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ct-name">Name*</Label>
                <Input
                  id="ct-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="ct-email">Email*</Label>
                <Input
                  id="ct-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="ct-phone">Phone</Label>
                <Input
                  id="ct-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="ct-subject">Subject</Label>
                <Input
                  id="ct-subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="ct-msg">Message*</Label>
                <Textarea
                  id="ct-msg"
                  data-testid="contact-message-input"
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] disabled:opacity-60 text-white font-medium px-6 py-3.5 rounded-md"
            >
              <Send className="w-4 h-4" />
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
